import { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Stack,
  Paper,
  Container,
  Typography,
  IconButton,
  Box,
  Divider,
  TextField,
  Modal,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Chip,
  Button,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { Form, FormikProvider, useFormik } from 'formik';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { useQuery } from '@tanstack/react-query';
import Iconify from '../../../components/iconify';
import { INITIAL_REIMBURSEMENT_FORM_STATE } from '../constants';
import ReimbursementRequestSchema from '../schema/reimbursementSchema';
import { getReviewers } from '../../../services/reimbursementService';
import { fileUpload } from '../../../services/fileUploadService';

const MAX_PDF_SIZE_BYTES = 10 * 1024 * 1024; // 10 MB

const ReimbursementRequestForm = ({ open, onClose, onSubmit, reimbursementData = null, isEdit = false }) => {
  const fileInputRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileError, setFileError] = useState('');
  const [isUploadingFile, setIsUploadingFile] = useState(false);

  const { data: reviewersResponse } = useQuery({
    queryKey: ['reimbursementReviewers'],
    queryFn: async () => {
      try {
        const response = await getReviewers();
        return response?.data?.data || [];
      } catch (error) {
        console.error('Error fetching reviewers:', error);
        return [];
      }
    },
    enabled: open,
  });

  const reviewersList = reviewersResponse || [];

  const getInitialValues = () => {
    if (isEdit && reimbursementData) {
      return {
        date: reimbursementData.date ? dayjs(reimbursementData.date) : null,
        description: reimbursementData.description || '',
        amount: reimbursementData.amount || '',
        attachmentKey: reimbursementData.attachmentKey || '',
        attachmentUrl: reimbursementData.attachmentUrl || '',
        reviewers: reimbursementData.reviewers || [],
      };
    }
    return INITIAL_REIMBURSEMENT_FORM_STATE;
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    setFileError('');
    if (!file) return;

    if (file.type !== 'application/pdf') {
      setFileError('Only PDF files are allowed');
      event.target.value = '';
      return;
    }
    if (file.size > MAX_PDF_SIZE_BYTES) {
      setFileError('PDF must be smaller than 10 MB');
      event.target.value = '';
      return;
    }
    setSelectedFile(file);
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setFileError('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    setSubmitting(true);
    try {
      let attachmentKey = values.attachmentKey || '';
      let attachmentUrl = values.attachmentUrl || '';

      if (selectedFile) {
        setIsUploadingFile(true);
        try {
          const uploadResult = await fileUpload('reimbursements', selectedFile.name, selectedFile);
          attachmentKey = uploadResult?.key || '';
          attachmentUrl = uploadResult?.location || '';
        } finally {
          setIsUploadingFile(false);
        }
      }

      if (!attachmentKey) {
        setFileError('PDF attachment is required');
        setSubmitting(false);
        return;
      }

      const submitData = {
        ...values,
        date: values.date ? dayjs(values.date).format('YYYY-MM-DD') : null,
        amount: parseFloat(values.amount),
        attachmentKey,
        attachmentUrl,
        reviewers: values.reviewers && values.reviewers.length > 0 ? values.reviewers : undefined,
      };

      await onSubmit(submitData);

      if (!isEdit) {
        resetForm({ values: INITIAL_REIMBURSEMENT_FORM_STATE });
        setSelectedFile(null);
      }
    } catch (error) {
      // Error handled by mutation's onError — preserve form state
    } finally {
      setSubmitting(false);
    }
  };

  const formik = useFormik({
    initialValues: getInitialValues(),
    validationSchema: ReimbursementRequestSchema(),
    onSubmit: handleSubmit,
    enableReinitialize: true,
  });

  const { values, errors, touched, isSubmitting, submitForm, getFieldProps, setFieldValue, resetForm } = formik;

  const handleClose = () => {
    onClose();
    resetForm({ values: INITIAL_REIMBURSEMENT_FORM_STATE });
    setSelectedFile(null);
    setFileError('');
  };

  return (
    <Modal open={open} onClose={handleClose} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '90%',
          maxWidth: 600,
          bgcolor: 'background.paper',
          boxShadow: 24,
          borderRadius: 2,
          maxHeight: '90vh',
          overflowY: 'auto',
        }}
      >
        <Paper sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              {isEdit ? 'Edit Reimbursement Request' : 'Request Reimbursement'}
            </Typography>
            <IconButton color="primary" onClick={handleClose}>
              <Iconify icon="ic:round-clear" />
            </IconButton>
          </Box>
          <Divider sx={{ mb: 3 }} />
          <Container maxWidth="sm">
            <FormikProvider value={formik}>
              <Form onSubmit={formik.handleSubmit}>
                <Stack spacing={3}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      label="Date *"
                      value={values.date ? dayjs(values.date) : null}
                      onChange={(newValue) => setFieldValue('date', newValue)}
                      inputFormat="DD-MMM-YYYY"
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          error={touched.date && Boolean(errors.date)}
                          helperText={touched.date && errors.date}
                          fullWidth
                        />
                      )}
                    />
                  </LocalizationProvider>

                  <TextField
                    label="Description *"
                    {...getFieldProps('description')}
                    inputProps={{ maxLength: 2000 }}
                    error={touched.description && Boolean(errors.description)}
                    helperText={
                      touched.description
                        ? errors.description
                        : `${values.description?.length || 0}/2000 characters`
                    }
                    multiline
                    rows={4}
                    fullWidth
                  />

                  <TextField
                    label="Amount *"
                    {...getFieldProps('amount')}
                    type="number"
                    inputProps={{ min: 0.01, step: 0.01 }}
                    error={touched.amount && Boolean(errors.amount)}
                    helperText={touched.amount && errors.amount}
                    fullWidth
                  />

                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      PDF Attachment *
                    </Typography>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="application/pdf"
                      onChange={handleFileChange}
                      style={{ display: 'none' }}
                    />
                    {isEdit && reimbursementData?.attachmentKey && !selectedFile && (
                      <Box sx={{ mb: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          Current attachment: PDF on file. Upload a new file to replace it.
                        </Typography>
                      </Box>
                    )}
                    {selectedFile ? (
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Iconify icon="material-symbols:picture-as-pdf" sx={{ color: 'error.main' }} />
                        <Typography variant="body2">{selectedFile.name}</Typography>
                        <IconButton size="small" color="error" onClick={handleRemoveFile}>
                          <Iconify icon="ic:round-clear" />
                        </IconButton>
                      </Stack>
                    ) : (
                      <Button
                        variant="outlined"
                        startIcon={<Iconify icon="material-symbols:upload" />}
                        onClick={() => fileInputRef.current?.click()}
                        size="small"
                      >
                        Upload PDF
                      </Button>
                    )}
                    {fileError && (
                      <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                        {fileError}
                      </Typography>
                    )}
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                      PDF only, max 10 MB
                    </Typography>
                  </Box>

                  <FormControl fullWidth error={touched.reviewers && Boolean(errors.reviewers)} required>
                    <InputLabel>Select Reviewers (Managers/HR) *</InputLabel>
                    <Select
                      multiple
                      value={values.reviewers || []}
                      onChange={(e) => {
                        const { value } = e.target;
                        setFieldValue('reviewers', typeof value === 'string' ? value.split(',') : value);
                      }}
                      renderValue={(selected) => {
                        if (!selected || selected.length === 0) return '';
                        return (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {selected.map((reviewerId) => {
                              const reviewer = reviewersList.find((r) => r.id === reviewerId);
                              return (
                                <Chip
                                  key={reviewerId}
                                  label={reviewer ? reviewer.userName : reviewerId}
                                  size="small"
                                />
                              );
                            })}
                          </Box>
                        );
                      }}
                      label="Select Reviewers (Managers/HR) *"
                    >
                      {reviewersList.map((reviewer) => (
                        <MenuItem key={reviewer.id} value={reviewer.id}>
                          {reviewer.userName} (
                          {reviewer.userRoleId === 2 ? 'Manager' : reviewer.userRoleId === 6 ? 'HR' : 'Admin'})
                        </MenuItem>
                      ))}
                    </Select>
                    {touched.reviewers && errors.reviewers && (
                      <FormHelperText>{errors.reviewers}</FormHelperText>
                    )}
                    {!errors.reviewers && (
                      <FormHelperText>
                        Select at least one reviewer. Admin and HR will automatically be included.
                      </FormHelperText>
                    )}
                  </FormControl>

                  <LoadingButton
                    fullWidth
                    size="large"
                    onClick={submitForm}
                    variant="contained"
                    loading={isSubmitting || isUploadingFile}
                  >
                    {isEdit ? 'Update Request' : 'Submit Request'}
                  </LoadingButton>
                </Stack>
              </Form>
            </FormikProvider>
          </Container>
        </Paper>
      </Box>
    </Modal>
  );
};

ReimbursementRequestForm.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  reimbursementData: PropTypes.object,
  isEdit: PropTypes.bool,
};

export default ReimbursementRequestForm;
